/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useLocation, useNavigate, useParams } from 'js/lib/useRouter';

import { Grid, Tab, TabContext, Tabs } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import DegreeHomeV2ExperienceButton from 'bundles/degree-home/components/DegreeHomeV2ExperienceButton';
import GoToDegreeAdmin from 'bundles/degree-home/components/GoToDegreeAdmin';
import { PATHS } from 'bundles/degree-home/components/v3-dpm/constants/constants';
import { showDegreeV2NavButtons } from 'bundles/degree-home/utils/experimentUtils';

import _t from 'i18n!nls/degree-home';

import { degreeV3ShouldBeShown } from './v3-dpm/lib/experiments';

type Props = {
  children: JSX.Element;
};

const styles = {
  tabNavigationContainer: (theme: Theme) => css`
    border-bottom: 2px solid ${theme.palette.gray[300]};
  `,

  tabNavigation: css`
    margin-bottom: -1px;
    border-bottom: none;
  `,

  buttonContainer: (theme: Theme, buttonVisible: boolean) => css`
    display: flex;
    padding: ${theme.spacing(8)} 0;

    ${theme.breakpoints.down('sm')} {
      padding: ${buttonVisible ? theme.spacing(8) : 0};

      button {
        width: 100%;
      }
    }
  `,

  contentContainer: (theme: Theme) => css`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 ${theme.spacing(48)};

    ${theme.breakpoints.down('xs')} {
      padding: 0;
    }
  `,
};

const TabsContainer: React.FunctionComponent<Props> = ({ children }) => {
  const { degreeSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const lastPathSegment = location.pathname.split('/').pop();
  const defaultTab = degreeV3ShouldBeShown(degreeSlug) ? PATHS.IN_PROGRESS : PATHS.HOME;

  const [selectedTab, setSelectedTab] = React.useState<string>(defaultTab);
  const showDegreeHomeV2ExperienceButton = selectedTab === PATHS.HOME && showDegreeV2NavButtons(degreeSlug);

  React.useEffect(() => {
    if (lastPathSegment) {
      setSelectedTab(lastPathSegment);
    }
  }, [lastPathSegment]);

  const handleTabChange = (newValue: string) => {
    setSelectedTab(newValue);
    navigate({
      pathname: `/degrees/${degreeSlug}/${newValue}`,
    });
  };

  const tabsProps = {
    css: styles.tabNavigation,
    'aria-label': _t('Degree home navigation'),
    onChange: handleTabChange,
  };

  return (
    <TabContext value={selectedTab}>
      <div css={styles.tabNavigationContainer}>
        <Grid container css={styles.contentContainer}>
          <Grid item xs={12} sm={6}>
            {degreeV3ShouldBeShown(degreeSlug) ? (
              <Tabs {...tabsProps}>
                <Tab label={_t('In Progress')} value={PATHS.IN_PROGRESS} />
                <Tab label={_t('Academics')} value={PATHS.ACADEMICS} />
              </Tabs>
            ) : (
              <Tabs {...tabsProps}>
                <Tab label={_t('In Progress')} value="home" data-e2e="degree-home-tab-link" />
                <Tab label={_t('Summary')} value="summary" data-e2e="degree-summary-tab-link" />
              </Tabs>
            )}
          </Grid>

          <Grid
            item
            container
            xs={12}
            sm={6}
            justifyContent="flex-end"
            alignItems="center"
            flex-direction="row"
            spacing={16}
          >
            {showDegreeHomeV2ExperienceButton && (
              <Grid item>
                <DegreeHomeV2ExperienceButton />
              </Grid>
            )}
            <Grid item>
              <GoToDegreeAdmin />
            </Grid>
          </Grid>
        </Grid>
      </div>

      <main id="main" css={styles.contentContainer}>
        {children}
      </main>
    </TabContext>
  );
};

export default TabsContainer;
